import React, { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { CheckCircle2Icon, Loader2Icon } from "lucide-react";
import { business, jailOptions } from "../../data/business";
type BailHelpFormProps = {
  tone?: "dark" | "light";
  defaultSubject?: string;
};
type Errors = Partial<Record<"name" | "phone" | "inmate", string>>;
export function BailHelpForm({
  tone = "dark",
  defaultSubject
}: BailHelpFormProps) {
  const onDark = tone === "dark";
  const [status, setStatus] = useState<"idle" | "sending" | "sent" | "error">("idle");
  const [errors, setErrors] = useState<Errors>({});
  const label = `block text-sm font-medium ${onDark ? "text-white/85" : "text-navy"}`;
  const field = `mt-2 block w-full min-h-[48px] rounded-xl border px-4 text-base transition-[border-color,box-shadow] duration-150 focus:outline-none focus:ring-2 ${onDark ? "border-white/15 bg-navy-900/60 text-white placeholder:text-white/40 focus:border-gold-soft focus:ring-gold-soft/30" : "border-navy/15 bg-white text-ink placeholder:text-muted/70 focus:border-gold focus:ring-gold/25"}`;
  const errText = onDark ? "text-[#F2C38B]" : "text-[#9A4A12]";
  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    if (data.get("company")) return; // honeypot
    const next: Errors = {};
    if (!String(data.get("name") || "").trim()) next.name = "Please enter your name.";
    const digits = String(data.get("phone") || "").replace(/\D/g, "");
    if (digits.length < 10) next.phone = "Please enter a 10-digit phone number.";
    if (!String(data.get("inmate") || "").trim()) next.inmate = "Please enter the person's name.";
    setErrors(next);
    if (Object.keys(next).length) return;
    setStatus("sending");
    window.setTimeout(() => setStatus("sent"), 900);
  }
  return <div aria-live="polite" data-id="element-98" data-mp="2e7fxiqu94zks">
      <AnimatePresence mode="wait" initial={false} data-id="element-99" data-mp="2e87tw3vob1c4">
        {status === "sent" ? <motion.div key="sent" initial={{
        opacity: 0,
        y: 8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className="py-6 text-center" data-id="element-100" data-mp="bk971qne1lss">
            <CheckCircle2Icon className="mx-auto h-12 w-12 text-live" strokeWidth={1.5} aria-hidden="true" data-id="element-101" data-mp="6kwld9896krs" />
            <h3 className={`mt-4 font-serif text-2xl ${onDark ? "text-white" : "text-navy"}`} data-id="element-102" data-mp="uaqx1ngu63ag">We've got your request.</h3>
            <p className={`mt-2 ${onDark ? "text-white/75" : "text-muted"}`} data-id="element-103" data-mp="z8siug5ncgpm">
              Expect a call back shortly. Urgent?{" "}
              <a href={business.phoneHref} className="font-semibold text-gold-soft underline-offset-4 hover:underline" data-id="element-104" data-mp="lcnbws9rj8j6">
                Call {business.phoneDisplay}
              </a>
              .
            </p>
          </motion.div> : <motion.form key="form" noValidate onSubmit={handleSubmit} initial={false} exit={{
        opacity: 0
      }} transition={{
        duration: 0.15
      }} className="grid gap-5 sm:grid-cols-2" data-id="element-105" data-mp="3b0nz1bl44wrz">
            {defaultSubject && <input type="hidden" name="subject" value={defaultSubject} data-id="element-106" data-mp="3ch1tsbwq0hzo" />}
            <div className="hidden" aria-hidden="true" data-id="element-107" data-mp="37hym0jpcvxo0">
              <label data-id="element-108" data-mp="37i8hgmt66x13">
                Company
                <input type="text" name="company" tabIndex={-1} autoComplete="off" data-id="element-109" data-mp="2jrutp8hmbkcb" />
              </label>
            </div>

            <div data-id="element-110" data-mp="2eu2t7q2wqpwi">
              <label htmlFor="bh-name" className={label} data-id="element-111" data-mp="2eumar5onbca0">Your Name</label>
              <input id="bh-name" name="name" autoComplete="name" className={field} aria-invalid={!!errors.name} aria-describedby={errors.name ? "bh-name-err" : undefined} data-id="element-112" data-mp="2ti7bisy1szer" />
              {errors.name && <p id="bh-name-err" className={`mt-1.5 text-sm ${errText}`} data-id="element-113" data-mp="2113h7y2c3dtp">{errors.name}</p>}
            </div>
            <div data-id="element-114" data-mp="1w3bk8pmu11in">
              <label htmlFor="bh-phone" className={label} data-id="element-115" data-mp="1w1qpaywlvgk9">Phone</label>
              <input id="bh-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" className={field} aria-invalid={!!errors.phone} aria-describedby={errors.phone ? "bh-phone-err" : undefined} data-id="element-116" data-mp="zby3kr3yiclv" />
              {errors.phone && <p id="bh-phone-err" className={`mt-1.5 text-sm ${errText}`} data-id="element-117" data-mp="18b1ntyev0y1y">{errors.phone}</p>}
            </div>
            <div data-id="element-118" data-mp="1d44hs7nju9io">
              <label htmlFor="bh-inmate" className={label} data-id="element-119" data-mp="1d4o400k8fk2i">Name of Person in Jail</label>
              <input id="bh-inmate" name="inmate" className={field} aria-invalid={!!errors.inmate} aria-describedby={errors.inmate ? "bh-inmate-err" : undefined} data-id="element-120" data-mp="1i1eiwkkaw9mc" />
              {errors.inmate && <p id="bh-inmate-err" className={`mt-1.5 text-sm ${errText}`} data-id="element-121" data-mp="1s15aaddke8i7">{errors.inmate}</p>}
            </div>
            <div data-id="element-122" data-mp="1w0mj9gs79ns4">
              <label htmlFor="bh-jail" className={label} data-id="element-123" data-mp="1vz33rsedbgre">Which Jail</label>
              <select id="bh-jail" name="jail" defaultValue="Not Sure" className={field} data-id="element-124" data-mp="3tsryy89zhrbi">
                {jailOptions.map(j => <option key={j} value={j} className="text-ink" data-id="element-125" data-mp="3q99lsy9rx0yd">
                    {j}
                  </option>)}
              </select>
            </div>
            <div className="sm:col-span-2" data-id="element-126" data-mp="3lh88ra4s767a">
              <label htmlFor="bh-msg" className={label} data-id="element-127" data-mp="3giyhham6gpqy">Message <span className={onDark ? "text-white/50" : "text-muted"} data-id="element-128" data-mp="3gjpzfimuqoyr">(optional)</span></label>
              <textarea id="bh-msg" name="message" rows={3} defaultValue={defaultSubject ? `${defaultSubject}: ` : undefined} className={`${field} py-3`} data-id="element-129" data-mp="3cjpnq5r50rve" />
            </div>
            <label className={`flex items-start gap-3 text-sm sm:col-span-2 ${onDark ? "text-white/70" : "text-muted"}`} data-id="element-130" data-mp="4107py5b8jns">
              <input type="checkbox" name="sms" className="mt-1 h-5 w-5 shrink-0 rounded accent-[#C9A24A]" data-id="element-131" data-mp="3v7ukgt9dnckp" />
              <span data-id="element-132" data-mp="2otlm23xif271">
                I agree to receive calls and text messages from Post Bail Bonds Inc. about my request. Message and data rates may apply. Reply STOP to opt out.
              </span>
            </label>
            <div className="flex flex-col gap-4 sm:col-span-2 sm:flex-row sm:items-center" data-id="element-133" data-mp="2l8jtf5jgw4t6">
              <button type="submit" disabled={status === "sending"} className="inline-flex min-h-[52px] items-center justify-center gap-2 rounded-full bg-gold px-7 font-semibold text-navy shadow-gold transition-[transform,background-color] duration-150 ease-out hover:-translate-y-0.5 hover:bg-gold-hover active:scale-[0.98] disabled:opacity-70" data-id="element-134" data-mp="2q73fd5y29glj">
                {status === "sending" && <Loader2Icon className="h-4 w-4 animate-spin" aria-hidden="true" data-id="element-135" data-mp="1sm7bfmzwofw2" />}
                {status === "sending" ? "Sending…" : "Request a Call Back"}
              </button>
              <p className={`text-sm ${onDark ? "text-white/70" : "text-muted"}`} data-id="element-136" data-mp="1jl97t5tdqae0">
                Prefer to talk?{" "}
                <a href={business.phoneHref} className={`font-semibold underline-offset-4 hover:underline ${onDark ? "text-gold-soft" : "text-navy"}`} data-id="element-137" data-mp="1okdftt4ki22r">
                  Call {business.phoneDisplay}
                </a>
                . We answer 24/7.
              </p>
            </div>
            <p className={`text-xs sm:col-span-2 ${onDark ? "text-white/40" : "text-muted/80"}`} data-id="element-138" data-mp="1dzi44yzewyf4">Protected by invisible spam filtering.</p>
          </motion.form>}
      </AnimatePresence>
    </div>;
}